import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatureFlag } from '@core/enums';
import { FieldDef } from '@core/models';
import { TsComponent } from '@modules/jupiter/@components/ts.component';
import { ObjectsManagerState } from '@modules/settings/objects-manager/state/state';
import { RelatedToType } from '@shared/enums';
import { HighlighterPipe } from '@shared/pipes/highlighter.pipe';
import { PipesModule } from '@shared/pipes/pipes.module';
import { AccountState } from '@state/account/state';
import isEqual from 'lodash-es/isEqual';
import { debounceTime, map, noop, Observable, Subject, switchMap, tap } from 'rxjs';
import { TsBadgeModule, TsButtonModule, TSIconModule, TsOverlayModule, TsPipesModule } from 'tsui';

@Component({
  selector: 'app-field-selector',
  standalone: true,
  imports: [
    TsButtonModule,
    TsOverlayModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatIconModule,
    TsBadgeModule,
    HighlighterPipe,
    TsPipesModule,
    PipesModule,
    AsyncPipe,
    FormsModule,
    TSIconModule,
    NgTemplateOutlet,
  ],
  templateUrl: './field-selector.component.html',
  styleUrl: './field-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldSelectorComponent extends TsComponent implements OnInit {
  expanded = signal<string[]>([]);
  columns = input([], {
    transform: (value: string[]) => {
      this.selectedColumns.set(value);
      return value;
    },
  });
  toggleType = input<'button' | 'eye'>('button');
  overrideNestedOnValue = input<boolean | undefined>(undefined);
  singleSelect = input(false);
  disabled = input(false);
  includeTimeSeries = input(false);
  relatedTo = input(null, {
    transform: (value: RelatedToType | string) => {
      this.fieldDefs$ = this.store.select(value).pipe(
        switchMap(() => {
          const selectorOptions = {
            allowedFieldsOnly: true,
            addNestedFields: true,
          };
          const selector = this.includeTimeSeries()
            ? ObjectsManagerState.getFieldsByObjectNameWithTimeSeries(this.relatedTo(), selectorOptions)
            : ObjectsManagerState.getFieldsByObjectName(this.relatedTo(), selectorOptions);

          return this.store.select(selector);
        }),
        map((fieldDefs) => {
          if (!fieldDefs) {
            return [];
          }

          this._fieldDefs = fieldDefs;

          // The 'hidden' attribute is used to hide fields from the field selector since those fields are used internally only.
          return fieldDefs.filter((fieldDef) => !fieldDef?.hidden);
        }),
      );

      return value;
    },
  });
  fieldSelectionChanged = output<string[]>();
  onlySelected = new FormControl(false);
  onlySelectedValue: Observable<boolean>;
  fieldInput = new FormControl('');
  fieldInputValue: Observable<string>;
  fieldDefs$: Observable<FieldDef[]>;
  hasMatch = signal(true);
  selectedColumns = signal([]);

  private expandSearchDebouncer = new Subject<string | boolean>();
  private _fieldDefs: FieldDef[] = [];

  ngOnInit(): void {
    this.onlySelectedValue = this.onlySelected.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300),
      tap((value) => {
        if (!value) {
          this.hasMatch.set(true);
          this.expanded.set([]);
        } else {
          this.expandSearchDebouncer.next(value);
        }
      }),
    );

    this.fieldInputValue = this.fieldInput.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300),
      tap((value) => {
        if (!value) {
          this.hasMatch.set(true);
          this.expanded.set([]);
        } else {
          this.expandSearchDebouncer.next(value);
        }
      }),
    );

    this.expandSearchDebouncer
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(100),
        tap((value) => {
          let groups = [];

          if (typeof value === 'boolean') {
            groups = this._fieldDefs.filter((x) => this.selectedColumns().includes(x.name));
          } else {
            groups = this._fieldDefs.filter((x) => x.label.toLowerCase().includes(value.toLowerCase()));
          }

          groups = groups.map((x) => x.meta.group.key).reduce((p, c) => (p.includes(c) ? p : [...p, c]), []);

          this.hasMatch.set(groups.length > 0);
          this.expanded.set(groups);
        }),
      )
      .subscribe(noop);
  }

  onColumnSelectClosedByBackdropClick(): void {
    this.fieldInput.setValue('');
    this.expanded.set([]);
    this.onlySelected.setValue(false);

    if (!isEqual(this.columns(), this.selectedColumns())) {
      this.fieldSelectionChanged.emit(this.selectedColumns());
    }

    this.expanded.set([]);
  }

  toggleDisplayColumn(fieldName: string): void {
    if (this.singleSelect()) {
      const isSelected = this.selectedColumns().includes(fieldName);
      this.selectedColumns.set(isSelected ? [] : [fieldName]);
      return;
    }

    const idx = this.selectedColumns().findIndex((x) => x === fieldName);

    if (idx < 0) {
      this.selectedColumns.set([...this.selectedColumns(), fieldName]);
    } else {
      this.selectedColumns.set(this.selectedColumns().filter((x) => x !== fieldName));
    }
  }

  ownTypeFilterFn(
    value: FieldDef[],
    filter: { relatedTo: RelatedToType; query: string; onlySelected: boolean; columns: string[]; nestedOn: boolean },
  ): FieldDef[] {
    return value.filter(
      (x) =>
        x.meta.group.key === filter.relatedTo &&
        (filter.nestedOn ? (x.type !== 'record' ? true : x.meta.record_type !== RelatedToType.FileResource) : true) &&
        (filter.onlySelected === true ? filter.columns.includes(x.name) : true) &&
        (filter.query ? x.label.toLowerCase().includes(filter.query.toLowerCase()) : true),
    );
  }

  relatedTypeFilterFn(
    value: FieldDef[],
    filter: { relatedTo: RelatedToType; query: string; onlySelected: boolean; columns: string[]; nestedOn: boolean },
  ): FieldDef[] {
    if (filter.nestedOn === false) {
      return [];
    }

    return value.filter(
      (x) =>
        x.meta.group.key !== filter.relatedTo &&
        (filter.onlySelected === true ? filter.columns.includes(x.name) : true) &&
        (filter.query ? x.label.toLowerCase().includes(filter.query.toLowerCase()) : true),
    );
  }

  toggleExpand(group: string): void {
    const idx = this.expanded().findIndex((x) => x === group);

    if (idx < 0) {
      this.expanded.set([...this.expanded(), group]);
    } else {
      this.expanded.set(this.expanded().filter((x) => x !== group));
    }
  }

  countByType(data: { items: any[]; selectedItems: string[] }): { label: string; hasSelected: boolean } {
    const total = data.items.length;
    const selected = data.items.filter((x) => data.selectedItems.includes(x.name)).length;
    return { label: `${selected}`, hasSelected: selected > 0 };
  }
}
